import ArrowBackIcon from '@offline-mui/icons-material/ArrowBack'
import DeleteOutlinedIcon from '@offline-mui/icons-material/DeleteOutlined'
import DownloadIcon from '@offline-mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@offline-mui/material'
import { useEffect, useState } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { accountingRequest, formatMoney, jsonInit, when } from '../api'
import { DownloadLabel, downloadFrom } from '../download'
import { LeoHeader } from '../LeoHeader'
import { useLoggedInName } from '../loggedInUser'

// SND-14276: agency commission checks, in two steps like BSP close. Run adds an open run for an office and week ending
// (everything unpaid up to that date); Close Agency Commission writes one numbered check per agency and the check
// number into each RA transaction. An open run can be downloaded or deleted; a closed run has its Summary. The
// logged-in user is saved as who ran or closed it.

const RUN_PAGE = '/invoice/reports/commission-runs'

type Action = 'run' | 'close' | 'delete'

// The week shown for a week-ending date: the 7 days ending on it.
function weekText(weekEnding: string) {
  const [year, month, day] = String(weekEnding || '').split('-').map(Number)
  if (!year || !month || !day) return ''
  const start = new Date(Date.UTC(year, month - 1, day - 6)).toISOString().slice(0, 10)
  return `${start} to ${weekEnding}`
}

function headCell(label: string, right = false) {
  return (
    <TableCell key={label} align={right ? 'right' : 'left'} sx={{ color: 'common.white', fontWeight: 700, border: 0 }}>
      {label}
    </TableCell>
  )
}

function statusChip(status: string) {
  return <Chip size="small" color={status === 'closed' ? 'success' : 'warning'} label={status === 'closed' ? 'Closed' : 'Open'} />
}


const downloadFile = downloadFrom

function reportName(run: any) {
  return `${run.office}-agency-commission-${String(run.weekEnding).slice(2).replace(/-/g, '')}-run-${run.id}.csv`
}

// Confirms Close or Delete (and Run when nobody is logged in, to ask the name). The very first Close takes the first
// check number from the form (the number after Gestion's last check).
function ActionDialog({
  action,
  run,
  busy,
  firstNumberMissing,
  onCancel,
  onConfirm,
}: {
  action: Action | null
  run: any
  busy: boolean
  firstNumberMissing: boolean
  onCancel: () => void
  onConfirm: (name: string) => void
}) {
  const loggedIn = useLoggedInName()
  const [name, setName] = useState('')
  useEffect(() => {
    if (action) setName('')
  }, [action])
  if (!action) return null
  // The logged-in user is saved as who acted; the name is asked only when nobody is logged in.
  const askName = action !== 'delete' && !loggedIn
  const blocked = action === 'close' && firstNumberMissing
  const valid = (!askName || name.trim().length >= 2) && !blocked
  const title = action === 'run' ? 'Run the agency commission report' : action === 'close' ? 'Close Agency Commission' : 'Delete this run permanently'
  const button = action === 'run' ? 'Run' : action === 'close' ? 'Close Agency Commission' : 'Delete run'
  return (
    <Dialog open onClose={busy ? undefined : onCancel} maxWidth="xs" fullWidth>
      <Box
        component="form"
        onSubmit={(event: any) => {
          event.preventDefault()
          if (valid && !busy) onConfirm(action === 'delete' ? '' : loggedIn || name.trim())
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          {action === 'close' && run ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Writes {run.checkCount} check{run.checkCount === 1 ? '' : 's'} for {formatMoney(run.total)} ({run.office}, up to {run.weekEnding}) and puts
              each check number in the RA transactions it pays. It only goes ahead if the agency commission has not changed since the run.
            </Alert>
          ) : null}
          {action === 'delete' && run ? (
            <Alert severity="warning">
              Permanently removes run {run.id} ({run.office}, week ending {run.weekEnding}); it cannot be undone. No check was written,
              and client transactions are not changed.
            </Alert>
          ) : null}
          {askName ? (
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Your name"
              value={name}
              onChange={(event) => setName(event.target.value.slice(0, 60))}
            />
          ) : null}
          {blocked ? <Alert severity="error" sx={{ mt: 2 }}>Enter the first check number on the form first.</Alert> : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" color={action === 'delete' ? 'error' : 'warning'} disabled={!valid || busy}>
            {busy ? 'Working…' : button}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  )
}

// Reports → Agency commissions: the latest 10 runs, then the fields to run a new report.
export function CommissionChecksTab() {
  const [offices, setOffices] = useState<any[]>([])
  const [settings, setSettings] = useState<any>(null)
  const [office, setOffice] = useState('')
  const [weekEnding, setWeekEnding] = useState('')
  const [runs, setRuns] = useState<any[]>([])
  const loggedIn = useLoggedInName()
  const [pending, setPending] = useState<{ action: Action; run?: any } | null>(null)
  const [lastRunId, setLastRunId] = useState<number | null>(null)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  // Only before the very first close: Leo has no last check number yet, so Finance gives the first one.
  const [firstCheck, setFirstCheck] = useState('')
  const needsFirstNumber = Boolean(settings && !settings.nextCheckNumber)
  const firstNumberMissing = needsFirstNumber && !/^\d{1,10}$/.test(firstCheck)

  async function loadRuns() {
    const body = await accountingRequest('/commission-checks/runs?limit=10')
    setRuns(body.runs || [])
  }

  async function loadSettings() {
    const body = await accountingRequest('/commission-checks/settings')
    setSettings(body)
    return body
  }

  useEffect(() => {
    accountingRequest('/offices')
      .then((body) => setOffices(body.offices || []))
      .catch(() => setOffices([]))
    loadSettings()
      .then((body) => setWeekEnding(body.previousSunday || ''))
      .catch((err) => setError(err.message))
    loadRuns().catch((err) => setError(err.message))
  }, [])

  async function confirm(name: string, step = pending) {
    if (!step) return
    const { action, run } = step
    setBusy(action)
    setError('')
    setNotice('')
    try {
      let result: any
      if (action === 'run') {
        result = await accountingRequest('/commission-checks/runs', jsonInit('POST', { office, weekEnding, runBy: name }))
        setNotice(`Run ${result.id}: ${result.checkCount} check${result.checkCount === 1 ? '' : 's'} for ${formatMoney(result.total)} to close (${result.office}, up to ${result.weekEnding}).`)
      } else if (action === 'close') {
        result = await accountingRequest(
          `/commission-checks/runs/${run.id}/close`,
          jsonInit('POST', { closedBy: name, firstCheckNumber: needsFirstNumber ? firstCheck : undefined }),
        )
        const first = result.checks?.[0]?.checkNumber
        const lastCheck = result.checks?.[result.checks.length - 1]?.checkNumber
        setNotice(`Run ${result.id} closed by ${result.closedBy}: ${result.checks.length} check${result.checks.length === 1 ? '' : 's'}${first ? ` (${first} to ${lastCheck})` : ''}.${result.storageNote ? ` ${result.storageNote}` : ''}`)
        await loadSettings()
      } else {
        result = await accountingRequest(`/commission-checks/runs/${run.id}/delete`, jsonInit('POST', {}))
        setNotice(`Run ${result.id} deleted permanently.`)
      }
      setLastRunId(action === 'delete' ? null : result.id)
      setPending(null)
      await loadRuns()
    } catch (err: any) {
      setPending(null)
      setError(err.message)
    } finally {
      setBusy('')
    }
  }

  async function downloadReport(run: any) {
    setError('')
    setDownloadingId(run.id)
    try {
      await downloadFile(`/commission-checks/runs/${run.id}/report.csv`, reportName(run), 'Could not download the report.')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloadingId(null)
    }
  }


  return (
    <Box>
      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>Recent runs (latest 10)</Typography>
      <TableContainer component={Card} sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'info.main' }}>
              {[headCell('Run ID'), headCell('Run date/time'), headCell('Run by'), headCell('Office'), headCell('Week ending'), headCell('Status'), headCell('')]}
            </TableRow>
          </TableHead>
          <TableBody>
            {runs.map((run) => (
              <TableRow key={run.id} sx={{ bgcolor: run.id === lastRunId ? 'rgba(233, 180, 76, 0.18)' : undefined }}>
                <TableCell sx={{ fontWeight: 700 }}>{run.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(run.createdAt)}</TableCell>
                <TableCell>{run.runBy || '—'}</TableCell>
                <TableCell>{run.office}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{run.weekEnding}</TableCell>
                <TableCell>{statusChip(run.status)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {run.status === 'open' ? (
                    <>
                      <Link component="button" type="button" disabled={downloadingId === run.id} onClick={() => downloadReport(run)} sx={{ fontWeight: 600, mr: 2 }}>
                        <DownloadLabel busy={downloadingId === run.id}>Download Report</DownloadLabel>
                      </Link>
                      <Link component="button" type="button" onClick={() => setPending({ action: 'close', run })} sx={{ fontWeight: 700, mr: 1 }}>Close Agency Commission</Link>
                      <Tooltip title="Delete run">
                        <IconButton size="small" aria-label={`Delete run ${run.id}`} onClick={() => setPending({ action: 'delete', run })} sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}>
                          <DeleteOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <Link href={`${RUN_PAGE}/${run.id}`} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>Summary</Link>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!runs.length ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">No check runs yet.</Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="overline" color="info.main" sx={{ display: 'block', fontWeight: 700, mb: 1 }}>New agency commission report</Typography>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 1.5 }}>
            <TextField select size="small" label="Office" value={office} onChange={(event) => setOffice(event.target.value)} sx={{ minWidth: 240 }}>
              {offices.map((row) => (
                <MenuItem key={row.code} value={row.code}>{row.name || row.code}</MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              type="date"
              label="Week ending"
              value={weekEnding}
              onChange={(event) => setWeekEnding(event.target.value)}
              helperText={weekEnding ? `Week: ${weekText(weekEnding)}` : ' '}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: new Date().toLocaleDateString('en-CA') } }}
            />
            {needsFirstNumber ? (
              <TextField
                size="small"
                label="First check number"
                value={firstCheck}
                onChange={(event) => setFirstCheck(event.target.value.replace(/\D/g, '').slice(0, 10))}
                helperText="The number after Gestion's last check"
                slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 10 } }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ height: 40, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Next check number: <b>{settings?.nextCheckNumber || '…'}</b>
              </Typography>
            )}
            <Button variant="contained" color="warning" disabled={!office || !weekEnding || Boolean(busy)} onClick={() => (loggedIn ? confirm(loggedIn, { action: 'run' }) : setPending({ action: 'run' }))} sx={{ height: 40 }}>
              {busy === 'run' ? 'Running…' : 'Run'}
            </Button>
          </Stack>
          {busy ? <LinearProgress color="warning" sx={{ mt: 2 }} /> : null}
          {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
          {notice ? <Alert severity="success" sx={{ mt: 2 }} onClose={() => setNotice('')}>{notice}</Alert> : null}
        </CardContent>
      </Card>

      <ActionDialog
        action={pending?.action || null}
        run={pending?.run}
        busy={Boolean(busy)}
        firstNumberMissing={firstNumberMissing}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </Box>
  )
}

// /invoice/reports/commission-runs/:runId (opens in a new tab): one row per check with its agency, count of RA entries,
// commission, check number (opens the check's own page) and file (downloads that check's transactions). Download Text
// File gets every check in one text file.
export function CommissionRunPage() {
  const { runId } = useParams<{ runId: string }>()
  const [run, setRun] = useState<any>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState('')

  useEffect(() => {
    accountingRequest(`/commission-checks/runs/${encodeURIComponent(runId)}`)
      .then((body) => setRun(body))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [runId])

  async function download(key: string, path: string, fileName: string, failure: string) {
    setError('')
    setDownloading(key)
    try {
      await downloadFile(path, fileName, failure)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloading('')
    }
  }

  const textName = run ? `${run.office}-checks-${String(run.closedAt || run.createdAt).slice(2, 10).replace(/-/g, '')}-run-${run.id}.txt` : ''

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 3 }} />
      {loading ? <LinearProgress color="warning" /> : null}
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
      {run ? (
        <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Agency commission checks · Run {run.id}</Typography>
            {statusChip(run.status)}
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!run.checks.length || downloading === 'text'}
              onClick={() => download('text', `/commission-checks/runs/${run.id}/checks.txt`, textName, 'Could not download the text file.')}
            >
              <DownloadLabel busy={downloading === 'text'}>Download Text File</DownloadLabel>
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {run.office} · week ending {run.weekEnding} · run {when(run.createdAt)}{run.runBy ? ` by ${run.runBy}` : ''}
            {run.closedAt ? ` · closed ${when(run.closedAt)}${run.closedBy ? ` by ${run.closedBy}` : ''}` : ''} · {run.checks.length} check{run.checks.length === 1 ? '' : 's'} · {formatMoney(run.total)}
          </Typography>
          {run.storageNote ? <Alert severity="info" sx={{ mb: 2 }}>{run.storageNote}</Alert> : null}
          {run.status === 'open' ? <Alert severity="info" sx={{ mb: 2 }}>This run is open: its checks are written when it is closed.</Alert> : null}

          <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Summary</Typography>
          <TableContainer component={Card} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'info.main' }}>
                  {[
                    headCell('Agency code'),
                    headCell('Agency name'),
                    headCell('Agency address'),
                    headCell('Count', true),
                    headCell('Agency commission', true),
                    headCell('Check number'),
                    headCell('File'),
                  ]}
                </TableRow>
              </TableHead>
              <TableBody>
                {run.checks.map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{row.agencyCode}</TableCell>
                    <TableCell>{row.agencyName}</TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>{row.agencyAddress}</TableCell>
                    <TableCell align="right">{row.transactionCount}</TableCell>
                    <TableCell align="right">{formatMoney(row.amount)}</TableCell>
                    <TableCell>
                      <Link component={RouterLink} to={`${RUN_PAGE}/${run.id}/${row.checkNumber}`} sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {row.checkNumber}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        type="button"
                        title="Download this check's client transactions"
                        disabled={downloading === `file-${row.id}`}
                        onClick={() => download(`file-${row.id}`, `/commission-checks/checks/${row.id}/file`, row.fileName, 'Could not download the check file.')}
                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                      >
                        <DownloadLabel busy={downloading === `file-${row.id}`}>{row.fileName}</DownloadLabel>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
                {run.checks.length ? (
                  <TableRow sx={{ bgcolor: 'rgba(233, 180, 76, 0.18)' }}>
                    <TableCell colSpan={3} sx={{ fontWeight: 700 }}>
                      Total · {run.checks.length} check{run.checks.length === 1 ? '' : 's'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      {run.checks.reduce((sum: number, row: any) => sum + Number(row.transactionCount || 0), 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(run.total)}</TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  )
}

const CHECK_COLUMNS: { key: string; label: string; money?: boolean }[] = [
  { key: 'office', label: 'Office' },
  { key: 'telephone', label: 'Telephone' },
  { key: 'agencyCode', label: 'Agency Code' },
  { key: 'agencyName', label: 'Agency Name' },
  { key: 'invoiceNumber', label: 'Invoice' },
  { key: 'recordLocator', label: 'File' },
  { key: 'destination', label: 'Destination' },
  { key: 'passenger', label: 'Passenger' },
  { key: 'date', label: 'Date' },
  { key: 'contact', label: 'Contact' },
  { key: 'amount', label: 'Amount', money: true },
]

// /invoice/reports/commission-runs/:runId/:checkNumber: one check and the client transactions it paid.
export function CommissionCheckPage() {
  const { runId, checkNumber } = useParams<{ runId: string; checkNumber: string }>()
  const [check, setCheck] = useState<any>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)

  useEffect(() => {
    setLoading(true)
    setError('')
    accountingRequest(`/commission-checks/runs/${encodeURIComponent(runId)}/checks/${encodeURIComponent(checkNumber)}`)
      .then((body) => setCheck(body))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false))
  }, [runId, checkNumber])

  async function download() {
    setError('')
    setDownloading(true)
    try {
      await downloadFile(`/commission-checks/checks/${check.id}/file`, check.fileName, 'Could not download the check file.')
    } catch (err: any) {
      setError(err.message)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', py: 2 }}>
      <LeoHeader sx={{ mb: 2 }} />
      <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} component={RouterLink} to={`${RUN_PAGE}/${runId}`}>
        Run {runId} summary
      </Button>
      {loading ? <LinearProgress color="warning" sx={{ mt: 2 }} /> : null}
      {error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert> : null}
      {check ? (
        <>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Check {check.checkNumber} · Run {check.runId}</Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              color="warning"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={downloading}
              onClick={download}
              sx={{ textTransform: 'none', fontFamily: 'monospace' }}
            >
              <DownloadLabel busy={downloading}>{check.fileName}</DownloadLabel>
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {check.agencyCode} · {check.agencyName} · {check.agencyAddress} · {check.office} · week ending {check.weekEnding}
          </Typography>

          <Typography variant="overline" color="info.main" sx={{ fontWeight: 700 }}>Client transactions paid</Typography>
          <TableContainer component={Card} sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'info.main' }}>
                  {CHECK_COLUMNS.map((column) => headCell(column.label, column.money))}
                </TableRow>
              </TableHead>
              <TableBody>
                {check.transactions.map((row: any, index: number) => (
                  <TableRow key={`${row.paymentId}-${index}`}>
                    {CHECK_COLUMNS.map((column) => (
                      <TableCell key={column.key} align={column.money ? 'right' : 'left'} sx={{ whiteSpace: column.key === 'date' ? 'nowrap' : undefined }}>
                        {column.key === 'invoiceNumber' ? (
                          <Link href={`/invoice/${encodeURIComponent(row.invoiceNumber)}`} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 700 }}>
                            {row.invoiceNumber}
                          </Link>
                        ) : column.money ? formatMoney(row.amount) : column.key === 'date' ? when(row.date) : row[column.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: 'rgba(233, 180, 76, 0.18)' }}>
                  <TableCell colSpan={CHECK_COLUMNS.length - 1} sx={{ fontWeight: 700 }}>Check total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{formatMoney(check.amount)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Box>
  )
}
